<template>
  <Disclosure
    as="nav"
    class="w-full fixed top-0 z-50 h-14 flex items-center bg-rch-black dark:bg-surface-950"
  >
    <div class="w-full px-2">
      <div class="relative flex lg:h-12 items-center justify-between">
        <div class="absolute inset-y-0 left-0 flex items-center lg:hidden">
          <!-- Mobile hamburger button -->
          <button
            type="button"
            class="-m-2.5 p-2.5 text-white"
            @click="toggleSidebar"
            data-test-id="mobile-sidebar-toggle"
          >
            <Bars3Icon class="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        <div
          class="flex flex-1 items-center justify-center sm:items-stretch lg:justify-between"
        >
          <div class="flex shrink-0 pl-2 items-center">
            <router-link :to="{ name: 'home' }">
              <img
                class="h-6 w-auto"
                src="../assets/Reach-Logo-White.svg"
                alt="Reach Logo"
              />
            </router-link>
          </div>
          <!-- page title -->
          <div
            class="hidden lg:flex flex-col flex-1 items-center justify-center text-white p-2"
          >
            <h1 id="page-title" class="text-base font-semibold">
              {{ useRoute().meta?.displayName }}
            </h1>

            <p id="page-subtitle" class="text-xs font-light">
              {{ useRoute().meta?.pageDescription }}
            </p>
          </div>
        </div>
        <div
          class="absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0"
        >
          <button
            type="button"
            class="mr-2 inline-flex items-center justify-center rounded-md p-2 text-surface-0/90 dark:text-surface-0/90 hover:text-surface-0 dark:hover:text-surface-0 hover:bg-surface-0/10 dark:hover:bg-surface-0/10 focus:outline-none focus:ring-2 focus:ring-surface-400 focus:ring-offset-1 focus:ring-offset-rch-black"
            @click="toggleTheme"
            data-test-id="theme-toggle"
          >
            <span class="sr-only">Toggle theme</span>
            <SunIcon v-if="isDark" class="h-5 w-5" aria-hidden="true" />
            <MoonIcon v-else class="h-5 w-5" aria-hidden="true" />
          </button>

          <!-- Profile dropdown -->
          <Menu as="div" class="relative ml-3">
            <div>
              <MenuButton
                class="relative flex rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-surface-400 focus:ring-offset-1 focus:ring-offset-rch-black"
              >
                <span class="absolute -inset-1.5" />
                <span class="sr-only">Open user menu</span>
                <img
                  class="size-8 rounded-full"
                  :src="authState?.user?.profilePictureUrl ?? ''"
                  alt=""
                />
              </MenuButton>
            </div>
            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <MenuItems
                class="absolute right-0 z-10 mt-2 lg:w-56 origin-top-right rounded-md bg-surface-0 dark:bg-surface-900 px-2 py-1 shadow-lg ring-1 ring-black/5 dark:ring-white/10 focus:outline-none"
              >
                <MenuItem>
                  <a
                    class="block px-2 text-sm text-surface-700 dark:text-surface-200"
                    >Logged in as: <br /><b>{{
                      authState?.user?.name ?? ''
                    }}</b></a
                  >
                </MenuItem>
                <hr
                  class="h-0.5 mx-auto my-0.5 bg-surface-200 border-0 rounded md:my-0.5 dark:bg-surface-700"
                />
                <MenuItem v-slot="{ active }">
                  <a
                    href="#"
                    :class="[
                      active
                        ? 'bg-surface-100 dark:bg-surface-800 outline-none'
                        : '',
                      'block px-2 py-2 text-sm text-surface-700 dark:text-surface-200 rounded-md',
                    ]"
                    @click.prevent="showAdmin"
                    >Settings</a
                  >
                </MenuItem>
                <MenuItem v-slot="{ active }">
                  <a
                    href="#"
                    @click.prevent="handleLogout"
                    :class="[
                      active
                        ? 'bg-surface-100 dark:bg-surface-800 outline-none'
                        : '',
                      'block px-2 py-2 text-sm text-surface-700 dark:text-surface-200 rounded-md',
                    ]"
                    >Sign out</a
                  >
                </MenuItem>
              </MenuItems>
            </transition>
          </Menu>
        </div>
      </div>
    </div>
  </Disclosure>
</template>

<script setup lang="ts">
import {
  Disclosure,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from '@headlessui/vue'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import { AdminPortal, useFrontegg } from '@frontegg/vue'
import { Bars3Icon, MoonIcon, SunIcon } from '@heroicons/vue/24/outline'
import { useSidebar } from '@/composables/useSidebar'
import { useThemeStore } from '@/stores/themeStore'

const { authState } = useFrontegg()
const { toggleSidebar } = useSidebar()
const themeStore = useThemeStore()
const { isDark } = storeToRefs(themeStore)

const props = defineProps({
  menuItems: {
    type: Array,
  },
  logout: {
    type: Function,
    required: true,
  },
})

function showAdmin() {
  AdminPortal.show()
}

function handleLogout() {
  props.logout()
}

function toggleTheme() {
  themeStore.toggle()
}
</script>
